import { useFormik } from 'formik'
import { FC, useMemo } from 'react'
import { useQueryClient } from 'react-query'
import {
  applyApiFieldErrors,
  ButtonType,
  GenderType,
  MaskType,
  OptionsHelper,
  QUERIES,
  showError,
} from '_metronic/helpers'
import { useT } from '_metronic/i18n/Metronici18n'
import {
  Button,
  FormDateInput,
  FormInput,
  FormSelect,
  IOption,
  ListLoading,
  LoadingIndicator,
  useModal,
  useToast,
} from 'app/components'
import { usuarioCreateSchema } from './validation/usuarioCreateSchema'
import { UsuarioDtoCreateGrupo } from '../../core/_models'
import { createUsuario } from '../../core/_requests'

const CAMPOS_API = [
  'primeiroNome',
  'sobreNome',
  'email',
  'cpf',
  'cns',
  'telefone',
  'sexo',
  'nascimento',
  'nomeGrupo',
]

const vazioParaUndefined = (valor: string) => valor.trim() || undefined

export const UsuarioCreateModal: FC = () => {
  const { closeModal } = useModal()
  const { showToast } = useToast()
  const queryClient = useQueryClient()
  const { t } = useT()

  const formik = useFormik({
    initialValues: {
      primeiroNome: '',
      sobreNome: '',
      email: '',
      cpf: '',
      cns: '',
      telefone: '',
      nascimento: '',
      sexo: undefined as IOption<GenderType> | undefined,
      nomeGrupo: undefined as IOption<string> | undefined,
    },
    validationSchema: useMemo(() => usuarioCreateSchema(t), [t]),
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true)
      try {
        const data: UsuarioDtoCreateGrupo = {
          primeiroNome: values.primeiroNome.trim(),
          sobreNome: values.sobreNome.trim(),
          email: values.email.trim(),
          cpf: vazioParaUndefined(values.cpf),
          cns: vazioParaUndefined(values.cns),
          telefone: vazioParaUndefined(values.telefone),
          nascimento: values.nascimento,
          sexo: values.sexo?.value as GenderType,
          nomeGrupo: values.nomeGrupo?.value as string,
        }
        await createUsuario(data)
        closeModal()
        showToast({
          variant: 'success',
          title: t('usuario.criado'),
          description: t('usuario.criado_confirmacao_email'),
        })
        queryClient.invalidateQueries(QUERIES.USUARIOS_LIST)
      } catch (error) {
        const temOutroErro = applyApiFieldErrors(error, formik.setFieldError, CAMPOS_API)
        if (temOutroErro) showError(error)
      } finally {
        setSubmitting(false)
      }
    },
  })

  return (
    <form onSubmit={formik.handleSubmit} noValidate>
      <div className='row'>
        <div className='col-md-6'>
          <FormInput
            formik={formik}
            name='primeiroNome'
            entity='primeiroNome'
            className='mb-3'
            isRequired
          />
        </div>
        <div className='col-md-6'>
          <FormInput
            formik={formik}
            name='sobreNome'
            entity='sobrenome'
            className='mb-3'
            isRequired
          />
        </div>
        <div className='col-md-6'>
          <FormInput formik={formik} name='email' entity='email' className='mb-3' isRequired />
        </div>
        <div className='col-md-6'>
          <FormInput
            formik={formik}
            name='telefone'
            entity='telefone'
            className='mb-3'
            mask={MaskType.Phone}
          />
        </div>
        <div className='col-md-6'>
          <FormInput formik={formik} name='cpf' entity='cpf' className='mb-3' mask={MaskType.CPF} />
        </div>
        <div className='col-md-6'>
          <FormInput
            formik={formik}
            name='cns'
            label='CNS'
            placeholder='000 0000 0000 0000'
            className='mb-3'
            mask={MaskType.CNS}
          />
        </div>
        <div className='col-12'>
          <p className='text-gray-700 fs-7 mt-n2 mb-3'>{t('usuario.cpf_ou_cns')}</p>
        </div>
        <div className='col-md-6'>
          <FormDateInput
            formik={formik}
            name='nascimento'
            entity='data_nascimento'
            className='mb-3'
            isRequired
          />
        </div>
        <div className='col-md-6'>
          <FormSelect
            formik={formik}
            entity='sexo'
            name='sexo'
            options={OptionsHelper.generos}
            className='mb-3'
            isDisabled={formik.isSubmitting}
            isRequired
          />
        </div>
        <div className='col-md-6'>
          <FormSelect
            formik={formik}
            entity='grupo'
            name='nomeGrupo'
            fetchOptions={OptionsHelper.getOptionsGrupos}
            className='mb-3'
            isDisabled={formik.isSubmitting}
            isRequired
          />
        </div>
      </div>

      <div className='d-flex justify-content-center mt-8'>
        <Button
          type={ButtonType.Reset}
          onClick={closeModal}
          className='btn-secondary me-3'
          disabled={formik.isSubmitting}
        >
          {t('common.cancel')}
        </Button>

        <Button type={ButtonType.Submit} className='btn-primary' disabled={formik.isSubmitting}>
          {formik.isSubmitting ? <LoadingIndicator /> : t('common.send')}
        </Button>
      </div>
      {formik.isSubmitting && <ListLoading />}
    </form>
  )
}
